import { type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Calculator, Percent, SplitSquareHorizontal } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { DetailCard } from '@/admin/components/shared/detail-card'
import type {
  SettlementSummary,
  SettlementLineItem,
} from '@/admin/hooks/use-settlement'
import { useSettlementOptions } from '@/admin/hooks/use-settlement'
import {
  getUnproratedItemDetails,
  ledgerBadgeVariant,
  ledgerPillClass,
} from '@/admin/lib/settlement-options'
import { formatCurrency } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'

interface Props {
  summary: SettlementSummary
  lineItems?: SettlementLineItem[]
  currency?: string
}

/** A label on the left, a figure on the right - the Overview card row. */
function Row({
  label,
  value,
  total = false,
}: {
  label: ReactNode
  value: ReactNode
  total?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-2.5 last:border-b-0',
        total && 'bg-[color-mix(in_srgb,var(--ink)_4%,transparent)]',
      )}
    >
      <span
        className={cn(
          'min-w-0 text-[13px]',
          total ? 'text-[var(--ink-2)]' : 'text-[var(--ink-3)]',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'text-right tabular-nums text-[var(--ink)]',
          total ? 'text-[15px] font-medium' : 'text-[13px]',
        )}
      >
        {value}
      </span>
    </div>
  )
}

function useMoney(currency: string) {
  const { locale } = useFormatters()
  return (value: number | string | null | undefined) =>
    formatCurrency(Number(value) || 0, currency, locale)
}

export function SettlementCostSummaryCard({
  summary,
  currency = 'USD',
}: Omit<Props, 'lineItems'>) {
  const { t } = useTranslation()
  const money = useMoney(currency)
  const { data: options } = useSettlementOptions()
  // WPS-165 #12: material paid in full (not prorated) is broken out only when
  // present, under the tenant's own Item Detail label, so the rows reconcile
  // to the total.
  const nonOcMaterial = Number(summary.non_oc_material_subtotal) || 0
  const unprorated = getUnproratedItemDetails(options)
  const unproratedLabel = (options?.item_details ?? [])
    .filter((o) => unprorated.has(String(o.value).toUpperCase()))
    .map((o) => o.label)
    .filter(Boolean)
    .join(', ')
  const byItemDetail = summary.item_detail_subtotals ?? []

  return (
    <DetailCard
      title={t('settlement.costSummary.title', 'Cost Summary')}
      icon={<Calculator className="h-3.5 w-3.5" />}
      collapsible={false}
    >
      <Row
        label={t('settlement.costSummary.material', 'Material')}
        value={money(summary.material_subtotal)}
      />
      {nonOcMaterial > 0 && (
        <Row
          label={
            unproratedLabel ||
            t('settlement.costSummary.unproratedMaterial', 'Material paid in full')
          }
          value={money(nonOcMaterial)}
        />
      )}
      <Row
        label={t('settlement.costSummary.labor', 'Labor')}
        value={money(summary.labor_subtotal)}
      />
      <Row
        label={t('settlement.costSummary.other', 'Other')}
        value={money(summary.other_subtotal)}
      />
      <Row
        total
        label={t('settlement.costSummary.total', 'Total cost')}
        value={money(summary.settlement_total)}
      />
      {/* WP1-61: subtotals broken out per Item Detail, where a tenant uses them. */}
      {byItemDetail.length > 0 && (
        <>
          <div className="px-4 pb-1 pt-3 text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {t('settlement.costSummary.byItemDetail', 'By item detail')}
          </div>
          {byItemDetail.map((row) => (
            <Row key={row.item_detail} label={row.label} value={money(row.subtotal)} />
          ))}
        </>
      )}
    </DetailCard>
  )
}

/** The bar's colours match the ledger pills: primary green, the rest amber. */
function ledgerBarClass(variant: string): string {
  return variant === 'default'
    ? 'bg-[var(--status-ok)]'
    : 'bg-[var(--status-warn)] opacity-75'
}

/**
 * How the settlement splits across the tenant's ledgers (e.g. warranty vs
 * goodwill). Labels, colours and account numbers all come from data: the
 * ledger options and each line's routed account. Hidden when no line carries
 * a ledger.
 */
export function SettlementLedgerSplitCard({
  lineItems = [],
  currency = 'USD',
}: Omit<Props, 'summary'>) {
  const { t } = useTranslation()
  const money = useMoney(currency)
  const { data: options } = useSettlementOptions()

  const ledgers = useMemo(() => {
    const byKey = new Map<
      string,
      { key: string; label: string; variant: string; amount: number; accounts: Set<string> }
    >()
    for (const item of lineItems) {
      const key = item.ledger_type
      if (!key) continue
      const option = (options?.ledger_types ?? []).find((o) => o.value === key)
      const entry = byKey.get(key) ?? {
        key,
        label: option?.label ?? key,
        variant: ledgerBadgeVariant(key, options),
        amount: 0,
        accounts: new Set<string>(),
      }
      entry.amount += Number(item.extended_price) || 0
      if (item.ledger_account) entry.accounts.add(item.ledger_account)
      byKey.set(key, entry)
    }
    // The tenant's own ledger order, so the split reads the same on every claim;
    // a ledger the options do not list goes last.
    const order = (options?.ledger_types ?? []).map((o) => o.value)
    const rank = (key: string) => {
      const index = order.indexOf(key)
      return index === -1 ? order.length : index
    }
    return [...byKey.values()].sort((a, b) => rank(a.key) - rank(b.key))
  }, [lineItems, options])

  if (ledgers.length === 0) return null
  const total = ledgers.reduce((sum, l) => sum + l.amount, 0)
  const share = (amount: number) => (total > 0 ? (amount / total) * 100 : 0)

  return (
    <DetailCard
      title={t('settlement.ledgerSplit.title', 'Ledger Split')}
      icon={<SplitSquareHorizontal className="h-3.5 w-3.5" />}
      collapsible={false}
    >
      {total > 0 && (
        <div className="border-b-[0.5px] border-b-[var(--rule-light)] px-4 pb-3.5 pt-3">
          <div className="flex h-[5px] overflow-hidden rounded-[3px] bg-[var(--rule-light)]">
            {ledgers.map((ledger) => (
              <span
                key={ledger.key}
                className={cn('block', ledgerBarClass(ledger.variant))}
                // A computed share of the total - a runtime value, not a style.
                style={{ width: `${share(ledger.amount)}%` }}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between gap-3 text-[11px] text-[var(--ink-3)]">
            {ledgers.map((ledger) => (
              <span key={ledger.key}>
                {ledger.label} {share(ledger.amount).toFixed(1)}%
              </span>
            ))}
          </div>
        </div>
      )}
      {ledgers.map((ledger) => (
        <Row
          key={ledger.key}
          label={
            <span className="inline-flex items-center gap-2">
              <Badge variant="outline" className={ledgerPillClass(ledger.variant)}>
                {ledger.label}
              </Badge>
              {ledger.accounts.size > 0 && (
                <span className="text-[11px] tracking-[0.06em] text-[var(--ink-3)]">
                  {[...ledger.accounts].map((a) => `GL ${a}`).join(', ')}
                </span>
              )}
            </span>
          }
          value={money(ledger.amount)}
        />
      ))}
    </DetailCard>
  )
}

/** Proration, only for a claim whose policy prorates it. */
export function SettlementProrationCard({
  summary,
  currency = 'USD',
}: Omit<Props, 'lineItems'>) {
  const { t } = useTranslation()
  const money = useMoney(currency)
  const { locale } = useFormatters()
  const hasProration =
    summary.material_proration_pct !== null &&
    summary.material_proration_pct !== undefined
  if (!hasProration && !summary.proration_formula_code) return null
  // The calculated proration to two decimals, so the RS can check it (WPS-242).
  const twoDecimals = (value: unknown) =>
    Number(value).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  // Each rounded line's calculated quantity and the whole number it rounds to.
  const roundedLines = Object.entries(summary.line_prorations ?? {}).filter(
    ([, line]) => line.calculated_quantity != null,
  )

  // The tenant's own name for the formula; its code is internal. The Material
  // and Labor rows below already show what it covers.
  const formula = summary.proration_formula_code
    ? summary.proration_formula_name || summary.proration_formula_code
    : t('settlement.proration.notLinked', "Not linked to this claim's policy")
  const laborPct = summary.labor_proration_pct ?? summary.material_proration_pct
  // WPS-165 #12: lines no formula prorates are payable on top of the prorated
  // figure; show that total only when the two actually differ.
  const unproratedPayable =
    (Number(summary.non_oc_material_subtotal) || 0) +
    (Number(summary.other_subtotal) || 0)

  return (
    <DetailCard
      title={t('settlement.proration.title', 'Proration')}
      icon={<Percent className="h-3.5 w-3.5" />}
      collapsible={false}
    >
      <Row label={t('settlement.proration.formula', 'Formula')} value={formula} />
      {hasProration && summary.prorated_quantity_rounding && (
        <>
          <Row
            label={t('settlement.proration.materialQuantities', 'Material quantities')}
            value={
              summary.prorated_quantity_rounding === 'nearest'
                ? t('settlement.proration.roundedNearest', 'Rounded to the nearest whole unit, at least 1')
                : t('settlement.proration.roundedUp', 'Rounded up to whole units')
            }
          />
          {roundedLines.map(([uuid, line]) => (
            <Row
              key={uuid}
              label={line.description || t('settlement.costSummary.material', 'Material')}
              value={t('settlement.proration.roundedFrom', '{{calculated}} → {{rounded}}', {
                calculated: twoDecimals(line.calculated_quantity),
                rounded: Number(line.quantity).toLocaleString(locale),
              })}
            />
          ))}
        </>
      )}
      {hasProration && (
        <>
          <Row
            label={t('settlement.proration.material', 'Material {{pct}}%', {
              pct: twoDecimals(summary.material_proration_pct),
            })}
            value={money(summary.prorated_material)}
          />
          <Row
            label={t('settlement.proration.labor', 'Labor {{pct}}%', {
              pct: twoDecimals(laborPct),
            })}
            value={money(summary.prorated_labor)}
          />
          <Row
            total
            label={t('settlement.proration.proratedTotal', 'Prorated total')}
            value={money(summary.prorated_total)}
          />
          {unproratedPayable > 0 && (
            <>
              <Row
                label={t(
                  'settlement.plusPaidInFull',
                  'Plus unprorated material and other lines, paid in full',
                )}
                value={money(unproratedPayable)}
              />
              <Row
                total
                label={t('settlement.payableTotal', 'Payable Total')}
                value={money(summary.payable_total)}
              />
            </>
          )}
        </>
      )}
    </DetailCard>
  )
}

/**
 * The Settlement tab's summary cards, laid out like the Overview tab: costs,
 * the ledger split and proration, each only where the claim has one.
 */
export function SettlementSummaryCard({
  summary,
  lineItems,
  currency = 'USD',
}: Props) {
  return (
    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
      <SettlementCostSummaryCard summary={summary} currency={currency} />
      <SettlementLedgerSplitCard lineItems={lineItems} currency={currency} />
      <SettlementProrationCard summary={summary} currency={currency} />
    </div>
  )
}
